import { useEffect, useState, useCallback } from 'react';
import { OrganizationApi, ToastStateHandler } from '../../../api';
import { useContainer } from '../../useContainer';
import { useTranslation } from '../../useTranslation';
import { BillingInvoiceDto } from '../../../components/BillingInvoicesTable';

interface BaseQuery {
    page?: number;
    size?: number;
    sort?: 'amount' | 'status' | 'billingDate';
    direction?: 'asc' | 'desc';
}

interface HookOptions extends BaseQuery {}

export const useHttpGetBillingInvoices = (options: HookOptions = {}) => {
    const { t } = useTranslation('newNotifications');
    const organizationApi: OrganizationApi = useContainer(OrganizationApi);
    const toastHandler = useContainer(ToastStateHandler);

    const [data, setData] = useState<BillingInvoiceDto[]>([]);
    const [currentPage, setCurrentPage] = useState(options.page ?? 1);
    const [isLoading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState<number | null>(null);

    const { sort, direction, size, page: optionsPage } = options;

    const effectivePage = optionsPage !== undefined ? optionsPage : currentPage;

    useEffect(() => {
        setLoading(true);

        const sizeToUse = size ?? 10;

        const { exec, abort } = organizationApi.getAllInvoicesByCustomerPaginated({
            query: {
                page: effectivePage,
                size: sizeToUse,
                sort,
                direction,
            },
            onSuccess: res => {
                const items = res.body.items;
                setData(items);
                if (res.body.total != null) {
                    setTotal(res.body.total);
                    setHasMore(items.length < res.body.total);
                } else {
                    setHasMore(items.length === sizeToUse);
                }
                setLoading(false);
            },
            onError: (_errorMessage, errorCode) => {
                if (errorCode === 404) {
                    setData([]);
                    setTotal(0);
                    setHasMore(false);
                } else {
                    toastHandler.push('error', t('common.error'), t('common.somethingWentWrong'));
                }
                setLoading(false);
            },
        });

        exec();

        return () => {
            if (abort) {
                abort();
            }
        };
    }, [effectivePage, sort, direction, size]);

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const refetch = useCallback(() => {
        setData([]);
        setHasMore(true);
        setCurrentPage(1);
    }, []);

    return {
        data,
        isLoading,
        hasMore,
        total,
        currentPage,
        setPage,
        refetch,
    };
};

export default useHttpGetBillingInvoices;
